import React, { useState, useCallback, useRef, useEffect } from 'react';
import { PlusIconPath } from './constants';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface Props {
  data: any;
  onChange: (data: any) => void;
}

const TrashIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
  </svg>
);

const QuestionIcon: React.FC<{ className?: string }> = ({ className = 'w-10 h-10' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" />
  </svg>
);

const AutoGrowTextarea: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
}> = ({ value, onChange, placeholder, className }) => {
  const ref = useRef<HTMLTextAreaElement>(null);

  const resize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.max(el.scrollHeight, 56) + 'px';
  }, []);

  useEffect(() => { resize(); }, [value, resize]);

  return (
    <textarea
      ref={ref}
      rows={2}
      value={value}
      onChange={(e) => { onChange(e.target.value); }}
      onInput={resize}
      placeholder={placeholder}
      className={className}
      style={{ overflow: 'hidden', resize: 'none' }}
    />
  );
};

const inputCls = 'flex h-10 w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 focus-visible:border-accent transition-colors';
const textareaCls = 'flex min-h-[56px] w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 focus-visible:border-accent transition-colors';

const FaqsEditor: React.FC<Props> = ({ data, onChange }) => {
  const form = data || { categories: [] };
  const [expandedCat, setExpandedCat] = useState<number | null>(0);
  const categories = React.useMemo(
    () => Array.isArray(form.categories) ? form.categories : [],
    [form.categories],
  );

  const emit = (newForm: any) => onChange(newForm);

  const orderedFaqIndices = React.useMemo(() => {
    const generalIdx = categories.findIndex((category: any) => category?.slug === 'general');
    const orderedCategoryIndices = generalIdx >= 0
      ? [generalIdx, ...categories.map((_: any, idx: number) => idx).filter((idx: number) => idx !== generalIdx)]
      : categories.map((_: any, idx: number) => idx);

    const nextMap = new Map<string, number>();
    let flatIdx = 0;

    orderedCategoryIndices.forEach((catIdx: number) => {
      const faqs = Array.isArray(categories[catIdx]?.faqs) ? categories[catIdx].faqs : [];
      faqs.forEach((_: any, faqIdx: number) => {
        nextMap.set(`${catIdx}:${faqIdx}`, flatIdx);
        flatIdx += 1;
      });
    });

    return nextMap;
  }, [categories]);

  const updateCat = (catIdx: number, field: string, value: any) => {
    const categories = [...form.categories];
    categories[catIdx] = { ...categories[catIdx], [field]: value };
    emit({ ...form, categories });
  };

  const updateFaq = (catIdx: number, faqIdx: number, field: string, value: string) => {
    const categories = [...form.categories];
    const faqs = [...categories[catIdx].faqs];
    faqs[faqIdx] = { ...faqs[faqIdx], [field]: value };
    categories[catIdx] = { ...categories[catIdx], faqs };
    emit({ ...form, categories });
  };

  const addFaq = (catIdx: number) => {
    const categories = [...form.categories];
    categories[catIdx] = {
      ...categories[catIdx],
      faqs: [...categories[catIdx].faqs, { question: '', answer: '' }],
    };
    emit({ ...form, categories });
  };

  const removeFaq = (catIdx: number, faqIdx: number) => {
    const categories = [...form.categories];
    const faqs = [...categories[catIdx].faqs];
    faqs.splice(faqIdx, 1);
    categories[catIdx] = { ...categories[catIdx], faqs };
    emit({ ...form, categories });
  };

  const addCategory = () => {
    const categories = [...(form.categories || []), {
      name: 'New Category',
      slug: 'new-category',
      faqs: [{ question: '', answer: '' }],
    }];
    emit({ ...form, categories });
    setExpandedCat(categories.length - 1);
  };

  const removeCategory = (catIdx: number) => {
    const categories = [...form.categories];
    categories.splice(catIdx, 1);
    emit({ ...form, categories });
    if (expandedCat === catIdx) setExpandedCat(null);
    else if (expandedCat !== null && expandedCat > catIdx) setExpandedCat(expandedCat - 1);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-lg font-display font-semibold text-primary">Frequently Asked Questions</h2>
          <p className="text-sm text-muted mt-1">{form.categories?.length || 0} categories (3-4 required)</p>
        </div>
        <button onClick={addCategory} className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 text-accent rounded-lg text-sm font-medium hover:bg-accent/20 transition-colors">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d={PlusIconPath} />
          </svg>
          Add Category
        </button>
      </div>

      {(!form.categories || form.categories.length === 0) && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <QuestionIcon className="w-10 h-10 text-zinc-300 mb-3" />
          <p className="text-sm text-muted mb-4">No FAQ categories yet</p>
          <button onClick={addCategory} className="px-4 py-2 bg-accent text-white rounded-lg text-sm font-medium hover:bg-accent/90 transition-colors">
            Add your first category
          </button>
        </div>
      )}

      {categories.map((cat: any, catIdx: number) => (
        <div key={catIdx} className="border border-zinc-200 rounded-lg overflow-hidden bg-white">
          <button
            onClick={() => setExpandedCat(expandedCat === catIdx ? null : catIdx)}
            className="w-full px-4 py-3 flex items-center justify-between bg-zinc-50 hover:bg-zinc-100 transition-colors text-left"
          >
            <span className="font-medium text-primary">{cat.name || `Category ${catIdx + 1}`} ({cat.faqs?.length || 0} Q&A)</span>
            <div className="flex items-center gap-2">
              <span
                role="button"
                onClick={(e) => { e.stopPropagation(); removeCategory(catIdx); }}
                className="p-1 text-zinc-400 hover:text-red-500 transition-colors"
                title="Remove category"
              >
                <TrashIcon />
              </span>
              <svg className={`w-4 h-4 text-muted transition-transform ${expandedCat === catIdx ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
              </svg>
            </div>
          </button>

          {expandedCat === catIdx && (
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Category Name</Label>
                  <Input value={cat.name || ''} onChange={(e) => updateCat(catIdx, 'name', e.target.value)} placeholder="e.g. Pricing, Emergency Service" />
                  <p className="text-xs text-muted">Groups related questions together</p>
                </div>
                <div className="space-y-1.5">
                  <Label>URL slug</Label>
                  <Input value={cat.slug || ''} onChange={(e) => updateCat(catIdx, 'slug', e.target.value)} placeholder="e.g. pricing" />
                  <p className="text-xs text-muted">Auto-generated — only change if needed</p>
                </div>
              </div>

              {(cat.faqs || []).map((faq: any, faqIdx: number) => (
                <div
                  key={faqIdx}
                  data-editor-key={`faqs.${orderedFaqIndices.get(`${catIdx}:${faqIdx}`) ?? faqIdx}`}
                  tabIndex={-1}
                  className="bg-zinc-100 rounded-lg p-3 space-y-2 focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted font-medium">Question {faqIdx + 1}</span>
                    <button
                      onClick={() => removeFaq(catIdx, faqIdx)}
                      className="p-1 text-zinc-400 hover:text-red-500 transition-colors"
                      title="Remove question"
                    >
                      <TrashIcon />
                    </button>
                  </div>
                  <input value={faq.question || ''} onChange={(e) => updateFaq(catIdx, faqIdx, 'question', e.target.value)}
                    placeholder="e.g. How much does it cost to fix a leaky faucet?"
                    className={inputCls} />
                  <AutoGrowTextarea
                    value={faq.answer || ''}
                    onChange={(v) => updateFaq(catIdx, faqIdx, 'answer', v)}
                    placeholder="Your answer — be specific and friendly"
                    className={textareaCls}
                  />
                </div>
              ))}
              <button onClick={() => addFaq(catIdx)} className="inline-flex items-center gap-1 text-sm text-accent hover:text-accent/80">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d={PlusIconPath} />
                </svg>
                Add Question
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default FaqsEditor;
